<template>
  <v-container class="white registered-teams-page" grid-list-xl fluid ma-0 pa-0>

    <!-- Modern Page Header with Stats -->
    <v-row class="mb-1">
      <v-col cols="12">
        <div class="modern-header">
          <div class="header-content">
            <div class="header-text">
              <h1 class="header-title">Registered Volleyball Teams</h1>
            </div>
          </div>
          <div class="stats-cards">
            <div class="stat-card stat-card-primary">
              <v-icon class="stat-icon">mdi-account-group</v-icon>
              <div class="stat-content">
                <div class="stat-value">{{ registeredTeams.length }}</div>
                <div class="stat-label">Total Teams</div>
              </div>
            </div>
            <div class="stat-card stat-card-success">
              <v-icon class="stat-icon">mdi-check-circle</v-icon>
              <div class="stat-content">
                <div class="stat-value">{{ approvedTeamsCount }}</div>
                <div class="stat-label">Approved</div>
              </div>
            </div>
            <div class="stat-card stat-card-warning">
              <v-icon class="stat-icon">mdi-clock-outline</v-icon>
              <div class="stat-content">
                <div class="stat-value">{{ pendingTeamsCount }}</div>
                <div class="stat-label">Pending</div>
              </div>
            </div>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Registered Teams Data Table -->
    <v-row class="mb-2">
      <v-col cols="12">
        <v-card class="teams-table-card modern-card" elevation="16">
          <div class="card-header">
            <div class="card-header-content">
              <div class="card-header-left">
                <v-icon class="header-icon" size="32" color="white">mdi-format-list-bulleted-square</v-icon>
                <h2 class="card-title">All Registered Teams</h2>
              </div>
            </div>
          </div>

          <v-card-text class="pa-6">
            <v-row class="mb-5">
              <v-col cols="12" md="10">
                <v-text-field
                  v-model="searchTable"
                  append-icon="mdi-magnify"
                  label="Search teams by name, captain, email, or phone..."
                  single-line
                  hide-details
                  outlined
                  dense
                  class="search-field"
                  color="primary"
                >
                  <template v-slot:prepend-inner>
                    <v-icon color="primary">mdi-database-search</v-icon>
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="12" md="2" class="text-right">
                <v-btn
                  color="success"
                  elevation="4"
                  class="export-btn"
                  @click="exportToExcel"
                  :loading="exporting"
                >
                  <v-icon left>mdi-microsoft-excel</v-icon>
                  Export to Excel
                </v-btn>
              </v-col>
            </v-row>

            <v-data-table
              :headers="tableHeaders"
              :items="registeredTeams"
              :search="searchTable"
              :loading="loadingTeams"
              loading-text="Loading registered teams..."
              class="elevation-0 teams-table modern-table"
              :items-per-page="10"
              :footer-props="{
                'items-per-page-options': [5, 10, 15, 20, -1]
              }"
              show-expand
              :expanded.sync="expandedRows"
              item-key="teamName"
            >
              <template v-slot:item.index="{ index }">
                <span class="team-number">{{ index + 1 }}</span>
              </template>

              <template v-slot:item.teamName="{ item }">
                <div class="team-name-cell">
                  <v-icon color="primary" class="mr-2">mdi-shield-account</v-icon>
                  <strong>{{ item.teamName }}</strong>
                </div>
              </template>

              <template v-slot:item.captain_name="{ item }">
                <div class="captain-cell">
                  <v-icon color="orange" size="18" class="mr-1">mdi-account-star</v-icon>
                  {{ item.captain_name }}
                </div>
              </template>

              <template v-slot:item.numberOfPlayers="{ item, expand, isExpanded }">
                <v-chip
                  :color="getPlayerCountColor(item.numberOfPlayers)"
                  dark
                  small
                  @click="expand(!isExpanded)"
                  class="player-chip-clickable"
                >
                  <v-icon left small>mdi-account-multiple</v-icon>
                  {{ item.numberOfPlayers }} Players
                  <v-icon right small>{{ isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                </v-chip>
              </template>

              <template v-slot:expanded-item="{ headers, item }">
                <td :colspan="headers.length" class="expanded-row-content">
                  <v-card class="ma-3 players-detail-card" elevation="4">
                    <v-card-title class="players-detail-header">
                      <v-icon color="white" class="mr-2">mdi-account-group</v-icon>
                      <span>Player Details for {{ item.teamName }}</span>
                    </v-card-title>
                    
                    <v-card-text class="pa-4">
                      <v-row v-if="item.players && item.players.length > 0">
                        <v-col 
                          v-for="player in item.players" 
                          :key="player.playerNumber"
                          cols="12"
                          sm="6"
                          md="4"
                        >
                          <v-card class="player-card" outlined>
                            <v-card-text class="pa-3">
                              <div class="player-number-badge">
                                Player {{ player.playerNumber }}
                              </div>
                              <div class="player-info">
                                <div class="player-info-row">
                                  <v-icon small color="primary" class="mr-2">mdi-account</v-icon>
                                  <span class="player-name">{{ player.name || 'N/A' }}</span>
                                </div>
                                <div class="player-info-row mt-2">
                                  <v-icon small color="green" class="mr-2">mdi-phone</v-icon>
                                  <span class="player-phone">{{ player.phone || 'N/A' }}</span>
                                </div>
                                <div class="player-info-row mt-2">
                                  <v-icon small color="blue" class="mr-2">mdi-email</v-icon>
                                  <span class="player-email">{{ player.email || 'N/A' }}</span>
                                </div>
                                <div class="player-info-row mt-2">
                                  <v-icon small color="orange" class="mr-2">mdi-account-child</v-icon>
                                  <span class="player-under18">Under 18: {{ player.under18 || 'N/A' }}</span>
                                </div>
                              </div>
                            </v-card-text>
                          </v-card>
                        </v-col>
                      </v-row>
                      
                      <div v-else class="no-players-message">
                        <v-icon size="48" color="grey lighten-1">mdi-account-off</v-icon>
                        <p class="mt-2 grey--text">No player details available</p>
                      </div>
                    </v-card-text>
                  </v-card>
                </td>
              </template>

              <template v-slot:item.captain_email="{ item }">
                <div class="contact-cell">
                  <v-icon size="16" class="mr-1">mdi-email</v-icon>
                  {{ item.captain_email }}
                </div>
              </template>

              <template v-slot:item.captain_phone="{ item }">
                <div class="contact-cell">
                  <v-icon size="16" class="mr-1">mdi-phone</v-icon>
                  {{ item.captain_phone }}
                </div>
              </template>

              <template v-slot:item.submitted="{ item }">
                <v-chip small outlined>
                  <v-icon left small>mdi-calendar</v-icon>
                  {{ item.submitted }}
                </v-chip>
              </template>

              <template v-slot:item.status="{ item }">
                <v-chip
                  :color="getStatusColor(item.status)"
                  dark
                  small
                >
                  {{ item.status }}
                </v-chip>
              </template>

              <template v-slot:item.action="{ item }">
                <v-btn
                  icon
                  color="red"
                  small
                  @click="showDeleteDialog(item)"
                >
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </template>

              <template v-slot:no-data>
                <div class="no-data-container">
                  <v-icon size="64" color="grey lighten-1">mdi-volleyball</v-icon>
                  <p class="mt-3 grey--text">No teams registered yet. Be the first to register!</p>
                </div>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Snackbar for messages -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="5000"
      top
    >
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="snackbar.show = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog.show" max-width="400">
      <v-card>
        <v-card-title class="delete-dialog-header">
          <v-icon color="white" class="mr-2">mdi-alert-circle</v-icon>
          <span>Confirm Delete</span>
        </v-card-title>

        <v-card-text class="pa-4">
          <p class="delete-message">
            Are you sure you want to delete the team <strong>{{ deleteDialog.teamName }}</strong>?
          </p>
          <p class="delete-warning">
            <v-icon small color="warning" class="mr-1">mdi-alert</v-icon>
            This action cannot be undone.
          </p>
        </v-card-text>

        <v-card-actions class="pa-4 pt-0">
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="cancelDelete"
            :disabled="deletingId !== null"
          >
            Cancel
          </v-btn>
          <v-btn
            color="red"
            dark
            @click="confirmDelete"
            :loading="deletingId !== null"
          >
            <v-icon left small>mdi-delete</v-icon>
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-container>
</template>

<script>
import ReportServices from "@/services/ReportServices";

export default {
  name: "RegisteredTeams",
  data() {
    return {
      loadingTeams: false,
      exporting: false,
      searchTable: '',
      registeredTeams: [],
      expandedRows: [],
      deletingId: null,
      deleteDialog: {
        show: false,
        teamId: null,
        teamName: ''
      },
      snackbar: {
        show: false,
        message: '',
        color: 'success'
      },
      tableHeaders: [
        { text: '#', value: 'index', sortable: false, width: '60px' },
        { text: 'Team Name', value: 'teamName', align: 'start' },
        { text: 'Captain', value: 'captain_name', sortable: false },
        { text: 'Players', value: 'numberOfPlayers', align: 'center' },
        { text: 'Email', value: 'captain_email', sortable: false },
        { text: 'Phone', value: 'captain_phone', sortable: false },
        { text: 'Registered On', value: 'submitted' },
        { text: 'Status', value: 'status', align: 'center' },
        { text: 'Action', value: 'action', sortable: false, align: 'center', width: '100px' }
      ]
    };
  },
  computed: {
    approvedTeamsCount() {
      return this.registeredTeams.filter(team =>
        team.status && team.status.toLowerCase() === 'approved'
      ).length;
    },
    pendingTeamsCount() {
      return this.registeredTeams.filter(team =>
        team.status && team.status.toLowerCase() === 'pending'
      ).length;
    }
  },
  methods: {
    async loadRegisteredTeams() {
      this.loadingTeams = true;
      try {
        const response = await ReportServices.getVolleyBallDetails();
        this.registeredTeams = response.data || [];
      } catch (error) {
        this.showSnackbar('Failed to load registered teams', 'error');
        this.registeredTeams = [];
      } finally {
        this.loadingTeams = false;
      }
    },
    getPlayerCountColor(count) {
      if (count >= 9) return 'green';
      if (count >= 6) return 'orange';
      return 'red';
    },
    getStatusColor(status) {
      const statusColors = {
        'pending': 'orange',
        'approved': 'green',
        'rejected': 'red',
        'confirmed': 'blue'
      };
      return statusColors[status.toLowerCase()] || 'grey';
    },
    showSnackbar(message, color) {
      this.snackbar.message = message;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },
    showDeleteDialog(item) {
      this.deleteDialog.teamId = item._id;
      this.deleteDialog.teamName = item.teamName;
      this.deleteDialog.show = true;
    },
    cancelDelete() {
      this.deleteDialog.show = false;
      this.deleteDialog.teamId = null;
      this.deleteDialog.teamName = '';
    },
    async confirmDelete() {
      const id = this.deleteDialog.teamId;
      this.deletingId = id;

      try {
        await ReportServices.deleteVolleyBallDetails(id);
        this.showSnackbar('Team deleted successfully!', 'success');
        this.deleteDialog.show = false;
        // Reload the teams list
        await this.loadRegisteredTeams();
      } catch (error) {
        this.showSnackbar('Failed to delete team', 'error');
      } finally {
        this.deletingId = null;
        this.deleteDialog.teamId = null;
        this.deleteDialog.teamName = '';
      }
    },
    exportToExcel() {
      this.exporting = true;

      try {
        // Prepare data for export
        const exportData = [];

        this.registeredTeams.forEach((team, teamIndex) => {
          // Add team header row
          exportData.push({
            'Team #': teamIndex + 1,
            'Team Name': team.teamName,
            'Captain Name': team.captain_name,
            'Captain Email': team.captain_email,
            'Captain Phone': team.captain_phone,
            'Status': team.status,
            'Registered On': team.submitted,
            'Player #': '',
            'Player Name': '',
            'Player Phone': '',
            'Player Email': '',
            'Under 18': ''
          });

          // Add player rows
          if (team.players && team.players.length > 0) {
            team.players.forEach((player) => {
              exportData.push({
                'Team #': '',
                'Team Name': '',
                'Captain Name': '',
                'Captain Email': '',
                'Captain Phone': '',
                'Status': '',
                'Registered On': '',
                'Player #': player.playerNumber || '',
                'Player Name': player.name || '',
                'Player Phone': player.phone || '',
                'Player Email': player.email || '',
                'Under 18': player.under18 || ''
              });
            });
          }

          // Add empty row between teams
          exportData.push({
            'Team #': '',
            'Team Name': '',
            'Captain Name': '',
            'Captain Email': '',
            'Captain Phone': '',
            'Status': '',
            'Registered On': '',
            'Player #': '',
            'Player Name': '',
            'Player Phone': '',
            'Player Email': '',
            'Under 18': ''
          });
        });

        // Convert to CSV
        const headers = ['Team #', 'Team Name', 'Captain Name', 'Captain Email', 'Captain Phone', 'Status', 'Registered On', 'Player #', 'Player Name', 'Player Phone', 'Player Email', 'Under 18'];
        const csvContent = [
          headers.join(','),
          ...exportData.map(row =>
            headers.map(header => {
              const value = row[header] || '';
              // Escape commas and quotes in values
              return `"${String(value).replace(/"/g, '""')}"`;
            }).join(',')
          )
        ].join('\n');

        // Create and download file
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `volleyball_teams_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        this.showSnackbar('Data exported successfully!', 'success');
      } catch (error) {
        this.showSnackbar('Failed to export data', 'error');
      } finally {
        this.exporting = false;
      }
    }
  },
  mounted() {
    this.loadRegisteredTeams();
  }
};
</script>

<style lang="scss" scoped>
.registered-teams-page {
  background: linear-gradient(135deg, #f5f7fa 0%, #e8eef5 100%);
  min-height: 100vh;
}

// Modern Header Styles
.modern-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 20px;
  padding: 10px;
  box-shadow: 0 20px 60px rgba(102, 126, 234, 0.4);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.15) 0%, transparent 70%);
    animation: pulse-bg 6s ease-in-out infinite;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #f093fb 0%, #f5576c 100%);
  }
}

@keyframes pulse-bg {
  0%, 100% {
    transform: scale(1) rotate(0deg);
    opacity: 0.5;
  }
  50% {
    transform: scale(1.2) rotate(180deg);
    opacity: 0.8;
  }
}

.header-content {
  display: flex;
  align-items: center;
  gap: 25px;
  margin-bottom: 2px;
  position: relative;
  z-index: 1;
}

.header-icon-wrapper {
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border: 2px solid rgba(255, 255, 255, 0.3);
}

.header-icon {
  font-size: 48px !important;
  color: white;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2));
}

.pulse-animation {
  animation: pulse-icon 2s ease-in-out infinite;
}

@keyframes pulse-icon {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

.header-text {
  flex: 1;
}

.header-title {
  font-size: 2rem;
  font-weight: 800;
  color: white;
  margin: 0;
  text-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  letter-spacing: -0.5px;
  line-height: 1.2;
}

.header-subtitle {
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.95);
  margin: 8px 0 0 0;
  font-weight: 400;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

// Stats Cards
.stats-cards {
  display: flex;
  gap: 20px;
  position: relative;
  z-index: 1;
}

.stat-card {
  flex: 1;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 14px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border: 2px solid rgba(255, 255, 255, 0.5);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
  }
}

.stat-card-primary {
  border-left: 5px solid #667eea;

  .stat-icon {
    color: #667eea;
    font-size: 48px !important;
  }

  .stat-value {
    color: #667eea;
  }
}

.stat-card-success {
  border-left: 5px solid #4caf50;

  .stat-icon {
    color: #4caf50;
    font-size: 48px !important;
  }

  .stat-value {
    color: #4caf50;
  }
}

.stat-card-warning {
  border-left: 5px solid #ff9800;

  .stat-icon {
    color: #ff9800;
    font-size: 48px !important;
  }

  .stat-value {
    color: #ff9800;
  }
}

.stat-content {
  flex: 1;
}

.stat-value {
  font-size: 2rem;
  font-weight: 800;
  line-height: 1;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 0.85rem;
  color: #666;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

// Data Table Card Styles
.modern-card {
  border-radius: 20px !important;
  overflow: hidden;
  box-shadow: 0 15px 50px rgba(0, 0, 0, 0.1) !important;
  border: 1px solid rgba(102, 126, 234, 0.1);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15) !important;
  }
}

.teams-table-card {
  .card-header {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    padding: 24px 30px;
    position: relative;

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 3px;
      background: linear-gradient(90deg, #f093fb 0%, #f5576c 100%);
    }
  }
}

.card-header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  z-index: 1;
}

.card-header-left {
  display: flex;
  align-items: center;
  gap: 15px;

  .card-title {
    color: white;
    font-size: 1.2rem;
    font-weight: 500;
    margin: 0;
    text-shadow: 0 3px 6px rgba(0, 0, 0, 0.3);
    letter-spacing: -0.5px;
  }
}

.team-count-chip {
  font-size: 1.1rem !important;
  font-weight: 700 !important;
  padding: 8px 20px !important;
  height: auto !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

// Export Button
.export-btn {
  font-weight: 700 !important;
  text-transform: none !important;
  letter-spacing: 0.5px;
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3) !important;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 6px 16px rgba(76, 175, 80, 0.4) !important;
    transform: translateY(-2px);
  }
}

// Search Field
.search-field {
  ::v-deep .v-input__slot {
    background: linear-gradient(135deg, #f8f9ff 0%, #ffffff 100%) !important;
    border: 2px solid #e0e7ff !important;
    border-radius: 12px !important;
    transition: all 0.3s ease;

    &:hover {
      border-color: #667eea !important;
      box-shadow: 0 4px 12px rgba(102, 126, 234, 0.1);
    }
  }

  ::v-deep .v-input__slot.v-input--is-focused {
    border-color: #667eea !important;
    box-shadow: 0 6px 20px rgba(102, 126, 234, 0.2) !important;
  }

  ::v-deep input {
    font-size: 1rem !important;
    font-weight: 500;
  }
}

// Modern Table Styles
.modern-table {
  ::v-deep .v-data-table-header {
    background: linear-gradient(135deg, #f8f9ff 0%, #e8eef5 100%);

    th {
      font-weight: 800 !important;
      font-size: 0.95rem !important;
      color: #667eea !important;
      text-transform: uppercase;
      letter-spacing: 1px;
      border-bottom: 3px solid #667eea !important;
      padding: 16px !important;
    }
  }

  ::v-deep tbody tr {
    transition: all 0.3s ease;
    border-bottom: 1px solid #e8eef5;

    &:hover {
      background: linear-gradient(135deg, #f8f9ff 0%, #ffffff 100%) !important;
      transform: translateX(5px);
      box-shadow: -5px 0 15px rgba(102, 126, 234, 0.1);
    }

    td {
      padding: 16px !important;
      font-size: 1rem;
      font-weight: 500;
    }
  }

  ::v-deep .v-data-footer {
    background: linear-gradient(135deg, #f8f9ff 0%, #ffffff 100%);
    border-top: 2px solid #e8eef5;
    padding: 16px;

    .v-data-footer__select {
      font-weight: 600;
    }
  }
}

// Table Cell Styles
.team-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 12px;
  font-weight: 800;
  font-size: 1.1rem;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.1) rotate(5deg);
    box-shadow: 0 6px 16px rgba(102, 126, 234, 0.4);
  }
}

.team-name-cell {
  display: flex;
  align-items: center;
  font-size: 1.1rem;

  strong {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    font-weight: 700;
  }
}

.captain-cell {
  display: flex;
  align-items: center;
  color: #555;
  font-weight: 600;
}

.contact-cell {
  display: flex;
  align-items: center;
  color: #666;
  font-size: 0.95rem;
  font-weight: 500;
}

.no-data-container {
  padding: 60px 20px;
  text-align: center;

  p {
    font-size: 1.1rem;
    margin: 0;
  }
}

// Player Chip Clickable
.player-chip-clickable {
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
}

// Expanded Row Styles
.expanded-row-content {
  background-color: #f8f9ff !important;
  padding: 0 !important;
}

.players-detail-card {
  border-radius: 12px !important;
  overflow: hidden;
}

.players-detail-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-size: 1.1rem;
  font-weight: 600;
  padding: 16px 20px;
}

.player-card {
  border-radius: 8px !important;
  border: 2px solid #e0e0e0 !important;
  transition: all 0.3s ease;
  height: 100%;

  &:hover {
    border-color: #667eea !important;
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.2);
    transform: translateY(-2px);
  }
}

.player-number-badge {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  display: inline-block;
  margin-bottom: 12px;
}

.player-info {
  margin-top: 8px;
}

.player-info-row {
  display: flex;
  align-items: center;
  font-size: 0.95rem;
}

.player-name {
  font-weight: 600;
  color: #333;
}

.player-phone {
  color: #666;
}

.player-email {
  color: #1976d2;
  font-weight: 500;
}

.player-under18 {
  color: #ff9800;
  font-weight: 600;
}

.no-players-message {
  text-align: center;
  padding: 40px 20px;

  p {
    font-size: 1rem;
    margin: 0;
  }
}

// Responsive Styles
@media (max-width: 960px) {
  .stats-cards {
    flex-direction: column;
  }

  .stat-card {
    width: 100%;
  }
}

@media (max-width: 768px) {
  .registered-teams-page {
    padding: 15px;
  }

  .modern-header {
    padding: 25px 20px;
    border-radius: 15px;
  }

  .header-content {
    flex-direction: column;
    text-align: center;
    gap: 15px;
  }

  .header-icon-wrapper {
    padding: 15px;
  }

  .header-icon {
    font-size: 40px !important;
  }

  .header-title {
    font-size: 1.5rem;
  }

  .header-subtitle {
    font-size: 0.9rem;
  }

  .stat-value {
    font-size: 2rem;
  }

  .stat-label {
    font-size: 0.85rem;
  }

  .card-header-content {
    flex-direction: column;
    gap: 15px;
    text-align: center;
  }

  .card-header-left {
    flex-direction: column;
    gap: 10px;

    .card-title {
      font-size: 1.3rem;
    }
  }

  .modern-table {
    ::v-deep .v-data-table-header th {
      font-size: 0.8rem !important;
      padding: 12px 8px !important;
    }

    ::v-deep tbody td {
      font-size: 0.9rem !important;
      padding: 12px 8px !important;
    }
  }

  .team-name-cell,
  .captain-cell,
  .contact-cell {
    font-size: 0.9rem;
  }

  .team-number {
    width: 35px;
    height: 35px;
    font-size: 1rem;
  }
}

// Delete Dialog Styles
.delete-dialog-header {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  color: white;
  font-size: 1.2rem;
  font-weight: 700;
  padding: 16px 20px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.delete-message {
  font-size: 1rem;
  color: #333;
  margin-bottom: 15px;

  strong {
    color: #f5576c;
    font-weight: 700;
  }
}

.delete-warning {
  display: flex;
  align-items: center;
  font-size: 0.9rem;
  color: #ff9800;
  margin: 0;
  padding: 10px;
  background-color: #fff3e0;
  border-radius: 6px;
  border-left: 4px solid #ff9800;
}
</style>

